import { IClauseType, IScopeClauseClause, TaskStatus } from "@/api";
import { IAIComparison, ICompareInChart } from "@/api/compare-in-chart";
import { ExcerptColumns } from "@/components/shared/ExcerptColumns";
import { AiComparisonExpandableWidget } from "@/components/shared/ExpandableAiComparison";
import { ExpandableClause } from "@/components/shared/ExpandableClause";
import { GuidanceExpandableWidget } from "@/components/shared/ExpandableGuidance";
import { inheritedGuidanceLabel } from "@/utils/inheritedGuidanceLabel";
import {
    ResolvedOrganizationClauseGuidance,
    resolveOrganizationClauseGuidance,
} from "@/utils/resolveClauseGuidance";
import { Box, styled } from "@mui/material";
import React from "react";

const ClausesContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    overflow: "visible",
}));

interface IClauseWithMeta {
    clause: IScopeClauseClause;
    aiComparison?: IAIComparison;
    hasGap: boolean;
    found: boolean;
}

const guidanceAttribution = (
    resolved: ResolvedOrganizationClauseGuidance
): string => {
    switch (resolved.source) {
        case "organization":
            return `Guidance updated by ${resolved.own.created_by_user?.first_name} ${resolved.own.created_by_user?.last_name}`;
        case "inherited_from_group":
            return inheritedGuidanceLabel(resolved.sharedByOrganizationName);
        case "default":
            return "Default guidance by InsureScope";
        default:
            resolved satisfies never;
            return "";
    }
};

interface ClausesListProps {
    chart: ICompareInChart;
    aiComparisons?: IAIComparison[];
    searchValue?: string;
    coverageFilter?: string;
    clauseTypeFilter?: string;
    expandedClauseIds?: Set<number>;
    onClauseToggle?: (clauseId: number) => void;
}

export const ClausesList: React.FC<ClausesListProps> = ({
    chart,
    aiComparisons = [],
    searchValue = "",
    coverageFilter = "all",
    clauseTypeFilter = "all",
    expandedClauseIds,
    onClauseToggle,
}) => {
    // Get unique clauses from all scopes
    const clausesMap: Map<number, IClauseWithMeta> = new Map();

    chart.scopes.forEach((scope) => {
        scope.scope_clauses.forEach((scopeClause) => {
            if (!clausesMap.has(scopeClause.clause.id)) {
                clausesMap.set(scopeClause.clause.id, {
                    clause: scopeClause.clause,
                    aiComparison: aiComparisons.find(
                        (comp) => comp.clause_id === scopeClause.clause.id
                    ),
                    hasGap: false,
                    found: false,
                });
            }

            const clauseMeta = clausesMap.get(scopeClause.clause.id);

            // Check if any instance is not found (has gap)
            if (!scopeClause.found && clauseMeta) {
                clauseMeta.hasGap = true;
            } else if (scopeClause.found && clauseMeta) {
                clauseMeta.found = true;
            }
        });
    });

    let uniqueClauses = Array.from(clausesMap.values());

    // Apply search filter
    if (searchValue) {
        uniqueClauses = uniqueClauses.filter(({ clause }) => {
            const searchLower = searchValue.toLowerCase();

            // Check clause title
            if (clause.title.toLowerCase().includes(searchLower)) return true;

            // Check clause guidance
            if (clause.guidance?.toLowerCase().includes(searchLower))
                return true;

            // Check organization guidance
            if (
                clause.organization_clause_guidance?.guidance
                    ?.toLowerCase()
                    .includes(searchLower)
            )
                return true;

            if (
                clause.inherited_guidance?.guidance
                    ?.toLowerCase()
                    .includes(searchLower)
            )
                return true;

            // Check excerpts content across all scopes
            const hasMatchingExcerpt = chart.scopes.some((scope) =>
                scope.scope_clauses.some(
                    (scopeClause) =>
                        scopeClause.clause.id === clause.id &&
                        scopeClause.content?.toLowerCase().includes(searchLower)
                )
            );
            if (hasMatchingExcerpt) return true;

            // Check comments across all scopes
            const hasMatchingComment = chart.scopes.some((scope) =>
                scope.scope_clauses.some(
                    (scopeClause) =>
                        scopeClause.clause.id === clause.id &&
                        scopeClause.comments?.some((comment) =>
                            comment.comment.toLowerCase().includes(searchLower)
                        )
                )
            );
            if (hasMatchingComment) return true;

            // Check scope-specific guidance
            const hasMatchingGuidance = chart.scopes.some((scope) =>
                scope.scope_clauses.some(
                    (scopeClause) =>
                        scopeClause.clause.id === clause.id &&
                        scopeClause.guidance
                            ?.toLowerCase()
                            .includes(searchLower)
                )
            );
            if (hasMatchingGuidance) return true;

            return false;
        });
    }

    // Apply coverage filter
    if (coverageFilter === "complete") {
        uniqueClauses = uniqueClauses.filter(
            ({ found, hasGap }) => found && !hasGap
        );
    } else if (coverageFilter === "gaps") {
        uniqueClauses = uniqueClauses.filter(({ hasGap }) => hasGap);
    }

    // Apply clause type filter
    if (clauseTypeFilter !== "all") {
        uniqueClauses = uniqueClauses.filter(({ clause }) =>
            clause.clause_types?.some(
                (type: IClauseType) => type.name === clauseTypeFilter
            )
        );
    }

    return (
        <ClausesContainer>
            {uniqueClauses.map(({ clause, aiComparison }) => {
                const aiComparisonProcessing =
                    !!aiComparison &&
                    (aiComparison.status === TaskStatus.IN_PROGRESS ||
                        aiComparison.status === TaskStatus.PENDING);

                const aiComparisonFailed =
                    !!aiComparison && aiComparison.status === TaskStatus.FAILED;

                const resolvedGuidance =
                    resolveOrganizationClauseGuidance(clause);

                return (
                    <ExpandableClause
                        key={clause.id}
                        title={clause.title}
                        description={clause.description}
                        policyType={clause.policy_type?.short_name}
                        clauseType={clause.clause_types?.[0]?.name}
                        isExpanded={expandedClauseIds?.has(clause.id)}
                        onToggle={() => onClauseToggle?.(clause.id)}
                    >
                        {/* AI Comparison Widget */}
                        {aiComparison && !aiComparisonFailed && (
                            <Box
                                sx={{
                                    backgroundColor: (theme) =>
                                        theme.palette.background.paper,
                                    padding: "16px",
                                    paddingBottom: "0",
                                }}
                            >
                                <AiComparisonExpandableWidget
                                    title={
                                        "AI Comparison" +
                                        (aiComparisonProcessing
                                            ? " processing..."
                                            : "")
                                    }
                                    comparisonText={
                                        aiComparison.comparison_text || ""
                                    }
                                />
                            </Box>
                        )}

                        {/* Guidance Widget */}
                        {resolvedGuidance.guidance && (
                            <Box
                                sx={{
                                    backgroundColor: (theme) =>
                                        theme.palette.background.paper,
                                    padding: "16px",
                                    paddingBottom: "0",
                                }}
                            >
                                <GuidanceExpandableWidget
                                    title="Guidance"
                                    guidance={resolvedGuidance.guidance}
                                    attribution={guidanceAttribution(
                                        resolvedGuidance
                                    )}
                                />
                            </Box>
                        )}
                        <ExcerptColumns
                            scopes={chart.scopes}
                            clauseId={clause.id}
                            clauseName={clause.title}
                        />
                    </ExpandableClause>
                );
            })}
        </ClausesContainer>
    );
};
